import React, { useCallback, useMemo, useRef, useState } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useHistory, useLocation } from "react-router";

import { faLocationCrosshairs, faSearch } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { debounce } from "lodash";

import { dispatchAnalyticsTrack } from "~/lib/segment";
import { withSuspense } from "~/lib/suspense";
import { useClearSearchParam } from "~/search/filters/Filter";
import { TextField } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import { FilterEmptySearchResult } from "./Filters";

import type { TrackerFilterByIdsQuery } from "~/__generated__/TrackerFilterByIdsQuery.graphql";
import type { TrackerFilterQuery } from "~/__generated__/TrackerFilterQuery.graphql";

import styles from "./AttendeeFilter.module.css";
import filtersStyles from "./Filters.module.css";

const trackersQuery = graphql`
	query TrackerFilterQuery($search: String) {
		smartTrackers(search: $search) {
			id
			name
		}
	}
`;

const trackersByIdQuery = graphql`
	query TrackerFilterByIdsQuery($smartTrackerIds: [ID!]!) {
		smartTrackersById(smartTrackerIds: $smartTrackerIds) {
			id
			name
		}
	}
`;

export type TrackerType = TrackerFilterQuery["response"]["smartTrackers"][0];

export const PARAM_NAME = "trackers";

function TrackerFilterTrigger({ selectedTrackerIds }: { selectedTrackerIds: string[] }) {
	const { smartTrackersById } = useLazyLoadQuery<TrackerFilterByIdsQuery>(
		trackersByIdQuery,
		{ smartTrackerIds: selectedTrackerIds },
		{ fetchPolicy: "store-or-network" },
	);

	return (
		<span className={styles.triggerContent}>
			<span>
				<FontAwesomeIcon icon={faLocationCrosshairs} />
			</span>
			{smartTrackersById.length > 2 ? (
				<span>
					{smartTrackersById[0].name} + {smartTrackersById.length - 1} others
				</span>
			) : smartTrackersById.length === 2 ? (
				<span>
					{smartTrackersById[0].name} + {smartTrackersById[1].name}
				</span>
			) : (
				<span>{smartTrackersById[0].name}</span>
			)}
		</span>
	);
}
export const TrackerFilterTriggerContent = withSuspense(<></>, TrackerFilterTrigger);

export function TrackerOption({
	tracker,
	selected,
	toggleTracker,
}: {
	tracker: TrackerType;
	selected: boolean;
	toggleTracker: (tracker: TrackerType) => void;
}) {
	const history = useHistory();
	const location = useLocation();

	const onCheckedChange = useCallback(
		(selected: boolean) => {
			const search = new URLSearchParams(location.search);
			const trackers = new Set(search.get(PARAM_NAME)?.split(",") || []);
			if (selected) {
				trackers.add(tracker.id);
			} else {
				trackers.delete(tracker.id);
			}
			if (trackers.size > 0) {
				search.set(PARAM_NAME, Array.from(trackers).join(","));
			} else {
				search.delete(PARAM_NAME);
			}
			history.replace({
				pathname: location.pathname,
				search: search.toString(),
			});

			toggleTracker(tracker);
		},
		[history, location.pathname, location.search, toggleTracker, tracker],
	);

	return (
		<Menu.CheckboxItem checked={selected} onCheckedChange={onCheckedChange} className={filtersStyles.filterOption}>
			{tracker.name}
		</Menu.CheckboxItem>
	);
}

export function TrackerRenderer({
	searchQuery,
	selectedTrackers,
	toggleTracker,
}: {
	searchQuery: string | undefined;
	selectedTrackers: string[];
	toggleTracker: (tracker: TrackerType) => void;
}) {
	const { smartTrackers } = useLazyLoadQuery<TrackerFilterQuery>(
		trackersQuery,
		{ search: searchQuery },
		{ fetchPolicy: "store-or-network" },
	);

	return (
		<>
			{smartTrackers.length > 0 ? (
				smartTrackers.map(tracker => (
					<TrackerOption
						key={tracker.id}
						tracker={tracker}
						selected={selectedTrackers.includes(tracker.id)}
						toggleTracker={toggleTracker}
					/>
				))
			) : (
				<FilterEmptySearchResult objectName="trackers" />
			)}
		</>
	);
}

export default function TrackerFilter() {
	const location = useLocation();
	const [searchQuery, setSearchQuery] = useState<string>();
	const [inputValue, setInputValue] = useState("");
	const [selectedTrackers, setSelectedTrackers] = useState<string[]>(() => {
		const search = new URLSearchParams(location.search);
		const selectedTrackers = search.get(PARAM_NAME)?.split(",") || [];
		return selectedTrackers;
	});

	const toggleTracker = useCallback(
		(tracker: TrackerType) => {
			const trackerId = tracker.id;
			setSelectedTrackers(trackers => {
				const index = trackers.indexOf(trackerId);
				if (index === -1) {
					dispatchAnalyticsTrack("Tracker Filter Applied", {
						// eslint-disable-next-line camelcase
						ux_location: location.pathname.slice(1), // Remove leading slash from pathname
					});
					return [...trackers, trackerId];
				}
				return trackers.filter(tracker => tracker !== trackerId);
			});
		},
		[location.pathname],
	);

	const debouncedSearchQuery = useMemo(() => {
		return debounce(value => {
			setSearchQuery(value);
		}, 350);
	}, []);

	const setValue = useCallback(
		newValue => {
			setInputValue(newValue);
			debouncedSearchQuery(newValue);
		},
		[debouncedSearchQuery],
	);

	const onTextFieldKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
		event.stopPropagation();
	}, []);

	const inputRef = useRef<HTMLInputElement>(null);

	const onInputFocus = useCallback(() => {
		inputRef.current?.focus();
	}, [inputRef]);

	const onClear = useClearSearchParam(PARAM_NAME);

	return (
		<Menu.Sub>
			<Menu.SubTrigger>Trackers</Menu.SubTrigger>
			<Menu.SubContent onFocus={onInputFocus}>
				<div className={styles.search}>
					<FontAwesomeIcon icon={faSearch} color="var(--secondaryTextColor)" className="search-icon" />
					<TextField
						value={inputValue}
						setValue={setValue}
						onKeyDown={onTextFieldKeyDown}
						ref={inputRef}
						placeholder="Search trackers"
						autoFocus
						className={styles.searchInput}
					/>
				</div>
				<Menu.Separator />
				<React.Suspense fallback={<span className={styles.loadingState}>Loading…</span>}>
					<TrackerRenderer
						searchQuery={searchQuery}
						selectedTrackers={selectedTrackers}
						toggleTracker={toggleTracker}
					/>
				</React.Suspense>
				{selectedTrackers.length !== 0 && (
					<>
						<Menu.Separator />
						<Menu.Item onSelect={onClear} className={filtersStyles.clearFilterOption}>
							Clear filter
						</Menu.Item>
					</>
				)}
			</Menu.SubContent>
		</Menu.Sub>
	);
}
